import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Upload, X, CheckCircle, AlertCircle, Loader, Film, Eye, EyeOff, Link } from 'lucide-react';
import { createMuxUpload, uploadVideoToMux, pollVideoStatus, updateVideoMetadata, saveVideoWithPlaybackId } from '../../../services/muxVideoService';

const MuxVideoUpload = ({ onVideoReady, onClose, songId = null, level = null, talentId = null }) => {
  const [mode, setMode] = useState('upload'); // 'upload' | 'existing'
  const [stage, setStage] = useState('select'); // select | uploading | processing | ready | error
  const [uploadProgress, setUploadProgress] = useState(0);
  const [videoData, setVideoData] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [published, setPublished] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [saving, setSaving] = useState(false);

  // Existing Mux ID mode state
  const [existingPlaybackId, setExistingPlaybackId] = useState('');
  const [existingTitle, setExistingTitle] = useState('');
  const [existingDescription, setExistingDescription] = useState('');
  const [existingPublished, setExistingPublished] = useState(false);

  const fileInputRef = useRef(null);

  // Drag state
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ mouseX: 0, mouseY: 0, posX: 0, posY: 0 });
  const modalRef = useRef(null);

  const handleDragStart = useCallback((clientX, clientY) => {
    setIsDragging(true);
    dragStart.current = {
      mouseX: clientX,
      mouseY: clientY,
      posX: position?.x,
      posY: position?.y,
    };
  }, [position]);

  const handleDragMove = useCallback((clientX, clientY) => {
    if (!isDragging) return;
    const dx = clientX - dragStart?.current?.mouseX;
    const dy = clientY - dragStart?.current?.mouseY;
    setPosition({
      x: dragStart?.current?.posX + dx,
      y: dragStart?.current?.posY + dy,
    });
  }, [isDragging]);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Mouse events
  const onMouseDown = (e) => {
    e?.preventDefault();
    handleDragStart(e?.clientX, e?.clientY);
  };

  // Touch events
  const onTouchStart = (e) => {
    const touch = e?.touches?.[0];
    handleDragStart(touch?.clientX, touch?.clientY);
  };

  useEffect(() => {
    if (!isDragging) return;

    const onMouseMove = (e) => handleDragMove(e?.clientX, e?.clientY);
    const onTouchMove = (e) => {
      const touch = e?.touches?.[0];
      handleDragMove(touch?.clientX, touch?.clientY);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', handleDragEnd);
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', handleDragEnd);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', handleDragEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  const handleFileSelect = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    if (!file?.type?.startsWith('video/')) {
      setError('Please select a valid video file.');
      return;
    }
    setSelectedFile(file);
    setError(null);
    if (!title) {
      setTitle(file?.name?.replace(/\.[^.]+$/, '')?.replace(/[-_]/g, ' '));
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setError(null);
    setStage('uploading');
    setUploadProgress(0);

    try {
      const { uploadUrl, videoId, video } = await createMuxUpload({
        title: title || selectedFile?.name,
        description,
        songId,
        level,
        talentId,
      });

      await uploadVideoToMux(uploadUrl, selectedFile, (pct) => {
        setUploadProgress(pct);
      });

      setStage('processing');
      setUploadProgress(100);

      setVideoData({ id: videoId, ...(video || {}), status: 'preparing' });

      const readyVideo = await pollVideoStatus(videoId, { maxAttempts: 72, intervalMs: 5000 });

      if (readyVideo?.status === 'errored') {
        throw new Error('Video processing failed on Mux.');
      }

      setVideoData({ ...readyVideo, id: videoId });
      setStage('ready');
    } catch (err) {
      console.error('Upload error:', err);
      setError(err?.message || 'Upload failed. Please try again.');
      setStage('error');
    }
  };

  const handleSaveMetadata = async () => {
    if (!videoData?.id) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await updateVideoMetadata(videoData?.id, { title, description, published });
      onVideoReady?.(updated);
    } catch (err) {
      setError(err?.message || 'Failed to save metadata.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveExistingId = async () => {
    const trimmedId = existingPlaybackId?.trim();
    if (!trimmedId) {
      setError('Please enter a Mux playback ID.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const saved = await saveVideoWithPlaybackId({
        muxPlaybackId: trimmedId,
        title: existingTitle?.trim() || trimmedId,
        description: existingDescription,
        published: existingPublished,
        songId,
        level,
        talentId,
      });
      onVideoReady?.(saved);
    } catch (err) {
      setError(err?.message || 'Failed to save video.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setStage('select');
    setUploadProgress(0);
    setVideoData(null);
    setSelectedFile(null);
    setError(null);
    setTitle('');
    setDescription('');
    setPublished(false);
  };

  const handleClose = () => {
    // If we're in the processing stage, pass back the partial video data
    // so MuxVideoSection can show it and poll for completion
    if (stage === 'processing' && videoData?.id) {
      onVideoReady?.(videoData);
    } else {
      onClose?.();
    }
  };

  const handleModeSwitch = (newMode) => {
    setMode(newMode);
    setError(null);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div
        ref={modalRef}
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg"
        style={{
          transform: `translate(${position?.x}px, ${position?.y}px)`,
          transition: isDragging ? 'none' : 'transform 0.05s ease',
          userSelect: isDragging ? 'none' : 'auto',
        }}
      >
        {/* Header - draggable */}
        <div
          className={`flex items-center justify-between p-5 border-b border-gray-200 rounded-t-xl ${
            isDragging ? 'cursor-grabbing' : 'cursor-grab'
          }`}
          onMouseDown={onMouseDown}
          onTouchStart={onTouchStart}
        >
          <div className="flex items-center gap-2 pointer-events-none">
            <Film className="w-5 h-5 text-indigo-600" />
            <h2 className="text-lg font-semibold text-gray-900">Add Video</h2>
            <span className="text-xs text-gray-400 ml-1">— drag to move</span>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors pointer-events-auto"
            onMouseDown={(e) => e?.stopPropagation()}
            type="button"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Mode Switcher — only show on select/error stage */}
          {(stage === 'select' || stage === 'error') && (
            <div className="flex rounded-lg border border-gray-200 overflow-hidden">
              <button
                type="button"
                onClick={() => handleModeSwitch('upload')}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors ${
                  mode === 'upload' ?'bg-indigo-600 text-white' :'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Upload className="w-4 h-4" />
                Upload File
              </button>
              <button
                type="button"
                onClick={() => handleModeSwitch('existing')}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors ${
                  mode === 'existing' ?'bg-indigo-600 text-white' :'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Link className="w-4 h-4" />
                Enter Mux ID
              </button>
            </div>
          )}

          {/* Error Banner */}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* ── MODE: Enter Existing Mux ID ── */}
          {mode === 'existing' && (stage === 'select' || stage === 'error') && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mux Playback ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={existingPlaybackId}
                  onChange={(e) => setExistingPlaybackId(e?.target?.value)}
                  placeholder="e.g. abcd1234efgh5678"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter the <code className="bg-gray-100 px-1 rounded">mux_playback_id</code> from an existing Mux asset.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={existingTitle}
                  onChange={(e) => setExistingTitle(e?.target?.value)}
                  placeholder="Video title (optional)"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={existingDescription}
                  onChange={(e) => setExistingDescription(e?.target?.value)}
                  placeholder="Optional description"
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-700">Published</p>
                  <p className="text-xs text-gray-500">Make this video visible to all users</p>
                </div>
                <button
                  type="button"
                  onClick={() => setExistingPublished(!existingPublished)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    existingPublished
                      ? 'bg-green-100 text-green-700 hover:bg-green-200' :'bg-gray-200 text-gray-600 hover:bg-gray-300'
                  }`}
                >
                  {existingPublished ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                  {existingPublished ? 'Published' : 'Draft'}
                </button>
              </div>

              <button
                type="button"
                onClick={handleSaveExistingId}
                disabled={saving || !existingPlaybackId?.trim()}
                className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium py-2.5 rounded-lg transition-colors text-sm"
              >
                {saving ? 'Saving...' : 'Save Video'}
              </button>
            </>
          )}

          {/* ── MODE: Upload File ── */}
          {mode === 'upload' && (
            <>
              {/* Stage: Select File */}
              {(stage === 'select' || stage === 'error') && (
                <>
                  <div
                    className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-indigo-400 hover:bg-indigo-50 transition-colors"
                    onClick={() => fileInputRef?.current?.click()}
                    onDragOver={(e) => e?.preventDefault()}
                    onDrop={(e) => {
                      e?.preventDefault();
                      const file = e?.dataTransfer?.files?.[0];
                      if (file) {
                        setSelectedFile(file);
                        if (!title) setTitle(file?.name?.replace(/\.[^.]+$/, '')?.replace(/[-_]/g, ' '));
                      }
                    }}
                  >
                    <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                    <p className="text-sm font-medium text-gray-700">
                      {selectedFile ? selectedFile?.name : 'Click or drag a video file here'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {selectedFile
                        ? `${(selectedFile?.size / 1024 / 1024)?.toFixed(1)} MB`
                        : 'MP4, MOV, WebM supported'}
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="video/*"
                      className="hidden"
                      onChange={handleFileSelect}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e?.target?.value)}
                      placeholder="Video title"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e?.target?.value)}
                      placeholder="Optional description"
                      rows={2}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                    />
                  </div>

                  <button
                    type="button"
                    onClick={handleUpload}
                    disabled={!selectedFile}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium py-2.5 rounded-lg transition-colors text-sm"
                  >
                    Upload to Mux
                  </button>
                </>
              )}

              {/* Stage: Uploading */}
              {stage === 'uploading' && (
                <div className="space-y-4 py-4">
                  <div className="flex items-center gap-3">
                    <Loader className="w-5 h-5 text-indigo-600 animate-spin" />
                    <p className="text-sm font-medium text-gray-700">Uploading to Mux...</p>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div
                      className="bg-indigo-600 h-2.5 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 text-right">{uploadProgress}%</p>
                </div>
              )}

              {/* Stage: Processing */}
              {stage === 'processing' && (
                <div className="flex flex-col items-center gap-3 py-8">
                  <Loader className="w-8 h-8 text-indigo-600 animate-spin" />
                  <p className="text-sm font-medium text-gray-700">Processing video on Mux...</p>
                  <p className="text-xs text-gray-500 text-center">
                    This may take a few minutes. You can safely close this popup — the video will appear once Mux finishes processing.
                  </p>
                </div>
              )}

              {/* Stage: Ready */}
              {stage === 'ready' && (
                <>
                  <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <p className="text-sm text-green-700 font-medium">Video is ready!</p>
                  </div>

                  {videoData?.thumbnail_url && (
                    <div className="rounded-lg overflow-hidden border border-gray-200">
                      <img
                        src={videoData?.thumbnail_url}
                        alt="Video thumbnail"
                        className="w-full h-40 object-cover"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e?.target?.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e?.target?.value)}
                      rows={2}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-700">Published</p>
                      <p className="text-xs text-gray-500">Make this video visible to all users</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setPublished(!published)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                        published
                          ? 'bg-green-100 text-green-700 hover:bg-green-200' :'bg-gray-200 text-gray-600 hover:bg-gray-300'
                      }`}
                    >
                      {published ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                      {published ? 'Published' : 'Draft'}
                    </button>
                  </div>

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleReset}
                      className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium py-2.5 rounded-lg transition-colors text-sm"
                    >
                      Upload Another
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveMetadata}
                      disabled={saving || !title?.trim()}
                      className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium py-2.5 rounded-lg transition-colors text-sm"
                    >
                      {saving ? 'Saving...' : 'Save Video'}
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MuxVideoUpload;
